import { useEffect, useRef, useState, type Dispatch, type SetStateAction, type FC } from 'react';
import { useCreateBettieMessage } from '../../../domains/bettie/ui/hooks/useCreateBettieMessage';
import type { components } from '../../../types/schema';
import { ArrowUpTailFill, ArrowUpTailStriped } from '@peacecover/icon';
import { t } from 'i18next';
import Bubble from '../../components/floating/Bubble';
import saveData from '../../../utils/saveData';
import { Trans } from 'react-i18next';
import { attachJosa } from '../../../utils/bettieUtils';

type ChatRoomMessage = components['schemas']['ChatRoomMessage'];
const STOP_REQUIRES_REPLY_BUBBLE_STORAGE_KEY = 'hasSeenStopRequiresReplyBubble';

export interface BettieMessageInputProps {
  chatRoomUuid: string;
  bettieNickname?: string;
  onMessageSent: (response?: ChatRoomMessage[]) => void;
  onBeforeSend?: (content: string) => void;
  onSendFailed?: () => void;
  disabled?: boolean;
  setFreeChatCount?: Dispatch<SetStateAction<number>>;
  onBettieReplies?: (ids: string[]) => void;
  showStopButton?: boolean;
}

const BettieMessageInput: FC<BettieMessageInputProps> = ({
  chatRoomUuid,
  bettieNickname = '',
  onMessageSent,
  onBeforeSend,
  onSendFailed,
  disabled = false,
  setFreeChatCount,
  onBettieReplies,
  showStopButton = false
}) => {
  const [message, setMessage] = useState('');
  const createMessage = useCreateBettieMessage();
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [lineCount, setLineCount] = useState(1);
  const didOptimisticallyDecrementRef = useRef(false);

  const MAX_ROWS = 3;
  const LINE_HEIGHT_PX = 18; // 라인 높이 기준값
  const MAX_COUNT = 300;

  const isMultiline = lineCount >= 2;
  const isDisabledOrSending = disabled || isSending;
  const isSendDisabled = !message.trim() || isDisabledOrSending;
  const placeholderText = `${disabled ? t('BettieChatRoom.disabledPlaceHolder') : isSending ? t('BettieChatRoom.isSendingPlaceHolder') : t('BettieChatRoom.activePlaceHolder')}`;
  const [shouldShowStopBubble, setShouldShowStopBubble] = useState(false);
  const [shouldShowStopRequiresReplyBubble, setShouldShowStopRequiresReplyBubble] = useState(false);
  const stopBubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const focusInput = () => textareaRef.current?.focus();

  const adjustTextareaSize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const maxHeight = LINE_HEIGHT_PX * MAX_ROWS;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    const estimatedLines = Math.max(1, Math.round(el.scrollHeight / LINE_HEIGHT_PX));
    setLineCount(estimatedLines);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adjustTextareaSize is dependent on message
  useEffect(() => {
    requestAnimationFrame(adjustTextareaSize);
  }, [message]);

  useEffect(() => {
    if (!showStopButton) return;
    if (typeof window === 'undefined') return;
    const hasSeen = localStorage.getItem('hasSeenStopChatBubble') === 'true';
    if (!hasSeen) {
      setShouldShowStopBubble(true);
    }
  }, [showStopButton]);

  useEffect(() => {
    return () => {
      if (stopBubbleTimerRef.current) clearTimeout(stopBubbleTimerRef.current);
    };
  }, []);

  const sendMessage = async (
    content: string,
    options?: {
      skipBettieResponse?: boolean;
      enableOptimisticUpdate?: boolean;
      clearInput?: boolean;
    }
  ) => {
    if (!content.trim() || isSending || disabled) return;

    const enableOptimisticUpdate = options?.enableOptimisticUpdate ?? true;
    const clearInput = options?.clearInput ?? true;

    if (enableOptimisticUpdate) {
      // 낙관적 업데이트를 위한 사전 콜백
      try {
        onBeforeSend?.(content.trim());
      } catch (_) {
        // biome-ignore lint/suspicious/noConsole: onBeforeSend failure logging
        console.error('Failed to create optimistic message', {
          function: 'BettieMessageInput',
          context: 'failed_to_create_optimistic_message',
          currentPath: `${window.location.pathname}${window.location.search}`,
          content: content.trim()
        });
      }

      if (setFreeChatCount) {
        setFreeChatCount((prev) => {
          if (prev > 0) {
            didOptimisticallyDecrementRef.current = true;
            return prev - 1;
          }
          return prev;
        });
      }
    } else {
      didOptimisticallyDecrementRef.current = false;
    }

    setIsSending(true);
    if (clearInput) {
      setMessage('');
    }
    // keep keyboard open by immediately refocusing the input
    requestAnimationFrame(focusInput);
    requestAnimationFrame(adjustTextareaSize);

    try {
      const requestBody = {
        message: {
          chat_room_id: chatRoomUuid,
          content: content.trim(),
          message_type: 'text',
          sent_at: new Date().toISOString(),
          ...(options?.skipBettieResponse ? { skip_bettie_response: true } : {})
        }
      } as const;

      const data = await createMessage.mutateAsync(requestBody);
      // ensure focus stays on the input after send completes
      requestAnimationFrame(focusInput);

      if (!data) {
        throw new Error('Empty response when creating message');
      }

      if (data.userMessage) {
        // 유저의 메시지가 response로 온 경우 refetch 처리
        onMessageSent();
      }

      // Bettie 응답 메시지 ID들을 부모에 전달하여 순차 노출 트리거
      try {
        const replyIds =
          (data.bettieMessages || [])
            .map((m) => m?.chatRoomMessageId)
            .filter((v): v is string => Boolean(v)) ?? [];
        if (replyIds.length > 0) {
          onBettieReplies?.(replyIds);
        }
      } catch (_) {
        // no-op
      }

      if (data.meta.bettieChatsCount !== undefined && data.meta.bettieChatsCount !== null) {
        // Avoid increasing the count with a potentially stale server value after an optimistic decrement
        setFreeChatCount?.((prev) => Math.min(prev, data.meta.bettieChatsCount as number));
      }
      didOptimisticallyDecrementRef.current = false;
    } catch (error) {
      onSendFailed?.();
      if (didOptimisticallyDecrementRef.current && setFreeChatCount) {
        setFreeChatCount((prev) => prev + 1);
      }
      didOptimisticallyDecrementRef.current = false;
      // biome-ignore lint/suspicious/noConsole: send failure logging
      console.error(
        'Failed to send message',
        error instanceof Error ? error : new Error(`Failed to send message: ${String(error)}`),
        {
          function: 'BettieMessageInput',
          context: 'failed_to_send_message',
          currentPath: `${window.location.pathname}${window.location.search}`,
          content: content.trim(),
          chat_room_uuid: chatRoomUuid
        }
      );
    } finally {
      setIsSending(false);
      // do not blur the input when sending finishes
      requestAnimationFrame(focusInput);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      sendMessage(message);
    }
  };

  const handleStopMessage = () => {
    if (!showStopButton) {
      if (typeof window === 'undefined') return;
      const hasSeen = localStorage.getItem(STOP_REQUIRES_REPLY_BUBBLE_STORAGE_KEY) === 'true';
      if (hasSeen) return;
      return;
    }
    if (!message.trim() || isDisabledOrSending) return;
    sendMessage(message, {
      skipBettieResponse: true,
      enableOptimisticUpdate: false,
      clearInput: true
    });

    const storageKey = STOP_REQUIRES_REPLY_BUBBLE_STORAGE_KEY;
    const hasSeen = localStorage.getItem(storageKey) === 'true';

    if (!hasSeen) {
      saveData(storageKey, 'true');

      setTimeout(() => {
        setShouldShowStopRequiresReplyBubble(true);
        setTimeout(() => {
          setShouldShowStopRequiresReplyBubble(false);
        }, 2500);
      }, 1000);
    }
  };

  const handleTextareaFocus = () => {
    if (shouldShowStopBubble) {
      setShouldShowStopBubble(false);
      saveData('hasSeenStopChatBubble', 'true');
    }
  };

  return (
    <div
      className={`flex px-[10px] pt-[8px] pb-[10px] gap-[4px] w-full ${isMultiline ? 'items-end' : 'items-center'}`}
    >
      {/* <div className={`w-[36px] h-[36px] ${isMultiline ? 'mb-[8px]' : ''}`}>
        <PlusCircle size={36} className="text-on-disable" />
      </div> */}
      <div
        className={`flex flex-1 min-w-0 gap-[6px] ${
          isMultiline
            ? 'rounded-[20px] px-[8px] py-[15px] items-end'
            : 'rounded-[30px] px-[8px] py-[15px]'
        } ${isDisabledOrSending ? 'bg-[#F7F7F7] dark:bg-disabled-dark' : 'bg-secondary-light dark:bg-secondary-dark'}`}
      >
        <div className="flex flex-col justify-center w-full">
          <textarea
            id="bettie-message-input"
            ref={textareaRef}
            value={message}
            rows={1}
            maxLength={MAX_COUNT}
            disabled={disabled}
            placeholder={placeholderText}
            className="bg-transparent ml-[8px] font-medium text-[15px] leading-[18px] resize-none outline-none placeholder:text-placeholder-light dark:placeholder:text-placeholder-dark [&::-webkit-scrollbar]:hidden"
            onFocus={handleTextareaFocus}
            onChange={(e) => {
              if (isSending) {
                return;
              }
              setMessage(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (message.trim()) {
                  sendMessage(message);
                }
              }
            }}
          />

          {/* 글자수 카운터 */}
          {message.length >= 200 && (
            <p className="text-[11px] text-on-warning mt-[8px] text-right">
              {message.length}/{MAX_COUNT}
            </p>
          )}
        </div>
      </div>
      <div className="relative shrink-0">
        {shouldShowStopBubble && (
          <div className="absolute top-[-40px] right-[1px]">
            <Bubble
              text={
                <Trans
                  i18nKey="BettieChatRoom.endConversationBubble"
                  components={{ span: <span className="font-bold" /> }}
                />
              }
              gradientDirection="right"
            />
          </div>
        )}
        {shouldShowStopRequiresReplyBubble && (
          <div className="absolute top-[-40px] right-[1px]">
            <Bubble
              text={
                <Trans
                  i18nKey="BettieChatRoom.stopRequiresReplyBubble"
                  values={{ nickname: attachJosa(bettieNickname, '가/이가') }}
                />
              }
              gradientDirection="right"
            />
          </div>
        )}
        <button
          type="button"
          disabled={isDisabledOrSending}
          aria-label="stop bettie response"
          aria-disabled={!showStopButton}
          onClick={handleStopMessage}
          onMouseDown={(e) => {
            // prevent focus loss on mobile
            e.preventDefault();
          }}
          tabIndex={-1}
          className="flex flex-col items-center gap-[3px] px-[4px]"
        >
          <div
            className={`p-[4px] rounded-full ${isSendDisabled || !showStopButton ? 'bg-surface-lv2' : 'bg-warningContainer'} w-fit`}
          >
            <ArrowUpTailStriped
              size={17}
              className={`${isSendDisabled || !showStopButton ? 'text-on-disable' : 'text-on-warning'}`}
            />
          </div>
          <p
            className={`text-[9px] text-text-main-lv3 leading-[11px] ${isSendDisabled || !showStopButton ? 'opacity-30' : ''}`}
          >
            STOP
          </p>
        </button>
      </div>
      <div className="flex flex-col items-center gap-[4px] px-[4px] shrink-0">
        <button
          type="button"
          disabled={isSendDisabled}
          onClick={handleSendMessage}
          onMouseDown={(e) => {
            // prevent the button from stealing focus, which would close the keyboard on mobile
            e.preventDefault();
          }}
          tabIndex={-1}
          className="shrink-0 inline-flex items-center justify-center w-[25px]"
        >
          <div
            className={`bg-primary-light dark:bg-primary-dark rounded-full p-[4px] ${isSendDisabled ? 'opacity-20' : ''}`}
          >
            <ArrowUpTailFill size={17} className="text-pure-white" />
          </div>
        </button>
        <img
          src="/assets/icons/graphic-3d/3D.like.png"
          alt="like"
          className={`w-[9px] h-[9px] flex-none ${isSendDisabled ? 'opacity-20' : 'opacity-100'}`}
        />
      </div>
    </div>
  );
};

export default BettieMessageInput;
